import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  SimpleFormField
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { ApiError, ProblemType, errorMessage } from '@/domains/api';
import { surveyApi } from '../api';
import type { IMyAssistantInvitation, IMyAssistants, IMyParticipant } from '../api/contracts';
import { formatDay } from '../format';

/**
 * Assistants (SR-487 ruling 14, survey-api's PFR §8; the owner's Q20), on the Account page: one
 * card for each person the account holds `self` — titled with the person's name only where there
 * is more than one — listing its assistants, whoever linked them, and the invitations still
 * pending, newest first as survey-api sends them. **Whether an invitation expired is read from
 * `expiresAt`**: survey-api keeps it `pending` past that until a sweep marks it, and lists it all
 * the same.
 *
 * "Invite an assistant" sends one address; survey-api answers 201 with a new invitation it mails,
 * or 200 with the pending one the address holds already, mailed nothing. The two are told apart
 * by the invitation's id against the list this card read — the client sees no status — so **the
 * form is drawn off until the list has been read** (Q26): before that the answer could not be
 * told. Revoke withdraws a pending invitation. **No assistant is removed here**: staff unlink one.
 * Every write reads the card's list again.
 *
 * Nothing about assistants is judged here: an address is survey-api's to accept (400 at `email`,
 * beside the box), and `already-linked`, `too-many-invitations` and `invitation-not-pending` are
 * said in the owner's words. A failed read of the account's people or of a person's assistants is
 * said, never drawn as "no assistants"; a re-read that fails over a list already read keeps that
 * list, under "Could not refresh" (Q26).
 */
export function AssistantsCards() {
  const [people, setPeople] = React.useState<Nullable<IMyParticipant[]>>(null);
  const [error, setError] = React.useState<Nullable<string>>(null);

  React.useEffect(() => {
    const controller = new AbortController();
    surveyApi
      .myParticipants(controller.signal)
      .then((held) =>
        setPeople(held.filter((one) => one.relation === 'self' && one.kind === 'person'))
      )
      .catch((loadError: unknown) => {
        if (!controller.signal.aborted) {
          setError(errorMessage(loadError, 'Your assistants could not be read'));
        }
      });
    return () => controller.abort();
  }, []);

  if (error) {
    return (
      <AssistantsFrame title="Assistants">
        <ReadFailure text={error} />
      </AssistantsFrame>
    );
  }
  if (people === null) return null;
  return (
    <>
      {people.map((person) => (
        <AssistantsCard
          key={person.participantId}
          person={person}
          title={people.length > 1 ? `Assistants for ${person.displayName}` : 'Assistants'}
        />
      ))}
    </>
  );
}

/** One person's assistants, its pending invitations, and the invitation form. */
function AssistantsCard({ person, title }: { person: IMyParticipant; title: string }) {
  const { participantId } = person;
  const [held, setHeld] = React.useState<Nullable<IMyAssistants>>(null);
  const [readError, setReadError] = React.useState<Nullable<string>>(null);
  const [reads, setReads] = React.useState(0);
  const [email, setEmail] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [fieldError, setFieldError] = React.useState<Nullable<string>>(null);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [failure, setFailure] = React.useState<Nullable<string>>(null);
  const emailId = React.useId();

  React.useEffect(() => {
    const controller = new AbortController();
    surveyApi
      .myAssistants(participantId, controller.signal)
      .then((answer) => {
        setHeld(answer ?? { assistants: [], invitations: [] });
        setReadError(null);
      })
      .catch((loadError: unknown) => {
        if (!controller.signal.aborted) {
          setReadError(errorMessage(loadError, 'Your assistants could not be read'));
        }
      });
    return () => controller.abort();
  }, [participantId, reads]);

  const reread = () => setReads((count) => count + 1);

  const invite = async (event: React.FormEvent) => {
    event.preventDefault();
    const address = email.trim();
    if (!address || held === null) return;
    setBusy(true);
    setFieldError(null);
    setFailure(null);
    setSaid(null);
    try {
      const answer = await surveyApi.inviteAssistant(participantId, address);
      const waiting = held.invitations.some((one) => one.invitationId === answer.invitationId);
      setSaid(
        waiting
          ? `${answer.email} already has an invitation waiting.`
          : `We have sent an invitation to ${answer.email}.`
      );
      setEmail('');
      reread();
    } catch (refusal) {
      if (refusal instanceof ApiError && refusal.isProblem(ProblemType.alreadyLinked)) {
        setFieldError(`${address} is already your assistant.`);
      } else if (
        refusal instanceof ApiError &&
        refusal.isProblem(ProblemType.tooManyInvitations)
      ) {
        setFailure('Five invitations are waiting. Revoke one to invite someone else.');
      } else if (refusal instanceof ApiError && refusal.isProblem(ProblemType.invalidBody)) {
        const at = (refusal.problem?.problems ?? []).filter((one) => one.target === 'email');
        if (at.length > 0) setFieldError(at.map((one) => one.reason).join(' '));
        else setFailure(errorMessage(refusal, 'The invitation was not sent'));
      } else {
        setFailure(errorMessage(refusal, 'The invitation was not sent'));
      }
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (invitation: IMyAssistantInvitation) => {
    setBusy(true);
    setFailure(null);
    setSaid(null);
    try {
      await surveyApi.revokeAssistantInvitation(participantId, invitation.invitationId);
    } catch (refusal) {
      setFailure(
        refusal instanceof ApiError && refusal.isProblem(ProblemType.invitationNotPending)
          ? 'That invitation was already accepted or withdrawn.'
          : errorMessage(refusal, 'The invitation was not withdrawn')
      );
    } finally {
      setBusy(false);
      reread();
    }
  };

  const now = Date.now();
  // Whether an answer is a new invitation is read off this list, so nothing is sent without it.
  const unread = held === null;

  return (
    <AssistantsFrame title={title} participantId={participantId}>
      {held !== null && readError && (
        <ReadFailure text="Could not refresh — showing the list as last read." />
      )}
      {held === null ? (
        readError && <ReadFailure text={readError} />
      ) : held.assistants.length + held.invitations.length === 0 ? (
        <p className="text-[0.9375rem] text-muted-foreground">You have no assistants.</p>
      ) : (
        <ul aria-label={title} className="grid gap-2 text-[0.9375rem]">
          {held.assistants.map((assistant) => (
            <li key={assistant.accountId} data-assistant={assistant.email} className="break-all">
              {assistant.email} — your assistant since {formatDay(assistant.linkedAt)}
            </li>
          ))}
          {held.invitations.map((invitation) => (
            <li
              key={invitation.invitationId}
              data-assistant-invitation={invitation.email}
              className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1"
            >
              <span className="break-all">
                {invitation.email} — {invitationWords(invitation, now)}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                aria-label={`Revoke the invitation to ${invitation.email}`}
                onClick={() => void revoke(invitation)}
              >
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      )}

      <form
        noValidate
        className="grid gap-3 sm:max-w-[32rem]"
        onSubmit={(event) => void invite(event)}
      >
        {/* `text` with the attributes `email` would carry: whether it is one plain address is
            survey-api's to say. */}
        <SimpleFormField
          label="Assistant’s email address"
          htmlFor={emailId}
          error={fieldError ?? undefined}
        >
          <Input
            id={emailId}
            type="text"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="off"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setFieldError(null);
            }}
            disabled={busy || unread}
            aria-invalid={fieldError !== null}
          />
        </SimpleFormField>
        <div>
          <Button type="submit" disabled={busy || unread || email.trim() === ''}>
            Invite an assistant
          </Button>
        </div>
      </form>

      {said && (
        <p role="status" className="text-[0.9375rem] text-muted-foreground">
          {said}
        </p>
      )}
      {failure && (
        <p role="alert" className="text-[0.9375rem] text-destructive">
          {failure}
        </p>
      )}
    </AssistantsFrame>
  );
}

/** The card around one person's assistants — its title, and the line the owner's Q20 gives. */
function AssistantsFrame({
  title,
  participantId,
  children
}: {
  title: string;
  participantId?: string;
  children: React.ReactNode;
}) {
  return (
    <Card data-assistants={participantId ?? ''}>
      <CardHeader>
        <CardTitle className="text-lg font-medium text-[var(--color-royal-blue)]">
          {title}
        </CardTitle>
        <CardDescription>
          An assistant signs in with their own email address and fills in your surveys for you.
          They do not see your numbers.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">{children}</CardContent>
    </Card>
  );
}

/** A pending invitation in words: expired by its date, else when it was sent and until when. */
function invitationWords(invitation: IMyAssistantInvitation, now: number): string {
  const expires = formatDay(invitation.expiresAt);
  return new Date(invitation.expiresAt).getTime() <= now
    ? `invitation expired ${expires}`
    : `invited ${formatDay(invitation.createdAt)}, expires ${expires}`;
}

function ReadFailure({ text }: { text: string }) {
  return (
    <Alert variant="destructive">
      <AlertDescription>{text}</AlertDescription>
    </Alert>
  );
}
